import { useCallback, useEffect, useMemo, useState } from "react";
import { AuditoriaService, buildAuditQueryParams } from "../services/auditoriaService";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { mapAuditLogItemsToRows } from "../utils/auditoriaMapper";
import {
  AUDITORIA_PAGE_SIZE,
  PAGINATION_SIZES,
} from "../constants/auditoria.constants";
import type { AuditoriaRow, AuditoriaFiltersState } from "../types/auditoria.types";
import { showPaymentExtendedTableColumns } from "../utils/showPaymentExtendedTableColumns";

const getDefaultFilters = (): AuditoriaFiltersState => ({
  flujo: "",
  accion: "",
  fechaInicio: "",
  fechaFinal: "",
  search: "",
  clientId: null,
  organizationId: null,
  paymentCurrency: "",
  paymentAmountMin: "",
  paymentAmountMax: "",
  confirmationSource: "",
});

function getPageNumbers(
  currentPage: number,
  lastPage: number,
  _canGoNext: boolean
): (number | string)[] {
  const maxPagesToShow = 7;
  if (lastPage <= maxPagesToShow) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", lastPage];
  }
  if (currentPage >= lastPage - 2) {
    return [1, "...", lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", lastPage];
}

export interface UseAuditoriaPageReturn {
  filters: AuditoriaFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<AuditoriaFiltersState>>;
  page: number;
  pageSize: number;
  rows: AuditoriaRow[];
  totalItems: number;
  lastPage: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  from: number;
  to: number;
  isLoading: boolean;
  error: string | null;
  paginationOptions: { label: string; value: string }[];
  pageNumbers: (number | string)[];
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (value: string) => void;
  handleViewHistory: (entityId: string, entityType: string) => void;
  handleCloseHistoryModal: () => void;
  historyModalOpen: boolean;
  historyEntityId: string;
  historyEntityType: string;
  refetch: () => void;
  noAccess: boolean;
  showPaymentExtendedTableColumns: boolean;
  showPayerNameInClientColumn: boolean;
}

export function useAuditoriaPage(): UseAuditoriaPageReturn {
  const [filters, setFilters] = useState<AuditoriaFiltersState>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<AuditoriaFiltersState>(getDefaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AUDITORIA_PAGE_SIZE);
  const [rows, setRows] = useState<AuditoriaRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEntityId, setHistoryEntityId] = useState("");
  const [historyEntityType, setHistoryEntityType] = useState("");

  const fetchAudit = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    try {
      const params = buildAuditQueryParams(appliedFilters, page, pageSize);
      const response = await AuditoriaService.fetchAudit(params);
      const data = response.data;
      setRows(mapAuditLogItemsToRows(data.data));
      setTotalItems(data.total);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
        setRows([]);
        setTotalItems(0);
        return;
      }
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Error al cargar los registros de auditoría";
      setError(message);
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    if (!hasAppliedFilters) {
      setRows([]);
      setTotalItems(0);
      setError(null);
      setIsLoading(false);
      return;
    }
    fetchAudit();
  }, [hasAppliedFilters, fetchAudit]);

  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const canGoPrevious = page > 1;
  const canGoNext = page < lastPage;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const paginationOptions = useMemo(
    () => PAGINATION_SIZES.map((size) => ({ label: String(size), value: String(size) })),
    []
  );

  const pageNumbers = useMemo(
    () => getPageNumbers(page, lastPage, canGoNext),
    [page, lastPage, canGoNext]
  );

  const showPaymentExtendedTableColumnsFlag = useMemo(
    () => showPaymentExtendedTableColumns(appliedFilters),
    [appliedFilters]
  );

  const showPayerNameInClientColumn = useMemo(
    () => Boolean(appliedFilters.clientId?.trim()),
    [appliedFilters.clientId]
  );

  const handleApplyFilters = useCallback(() => {
    setHasAppliedFilters(true);
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);
  const handleResetFilters = useCallback(() => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
    setHasAppliedFilters(false);
    setRows([]);
    setTotalItems(0);
    setError(null);
    setIsLoading(false);
  }, []);
  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handleItemsPerPageChange = useCallback((value: string) => {
    setPageSize(Number(value));
    setPage(1);
  }, []);
  const handleViewHistory = useCallback(
    (entityId: string, entityType: string) => {
      setHistoryEntityId(entityId);
      setHistoryEntityType(entityType);
      setHistoryModalOpen(true);
    },
    []
  );
  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryEntityId("");
    setHistoryEntityType("");
  }, []);

  return {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    handleViewHistory,
    handleCloseHistoryModal,
    historyModalOpen,
    historyEntityId,
    historyEntityType,
    refetch: useCallback(() => {
      if (hasAppliedFilters) fetchAudit();
    }, [hasAppliedFilters, fetchAudit]),
    noAccess,
    showPaymentExtendedTableColumns: showPaymentExtendedTableColumnsFlag,
    showPayerNameInClientColumn,
  };
}
